import { useIntl } from 'react-intl';

import { addressFormMessages } from './address-form-messages';
import { AutofillSelect } from './autofill-select';

import type { State } from '../types/reference-data';
import type { ComboboxParsedItem, OptionsFilter } from '@mantine/core';

type StatePickerProps = {
  value: string;
  onChange: (stateCode: string) => void;
  countryCode: string;
  label: string;
  states: State[];
  isLoadingStates: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  autocompleteSection?: string;
  'data-testid'?: string;
};

/**
 * State / province selector with substring search across both name and
 * code. Data (states list, loading flag) is injected via props so the
 * component has no dependency on any consumer's data-fetching library.
 */
export const StatePicker = ({
  value,
  onChange,
  countryCode,
  label,
  states,
  isLoadingStates,
  required = false,
  disabled = false,
  error,
  autocompleteSection,
  'data-testid': testId,
}: StatePickerProps) => {
  const intl = useIntl();

  const sortedStates = states.filter((s) => s.countryCode === countryCode).sort((a, b) => a.name.localeCompare(b.name, intl.locale));

  const selectData = sortedStates.map((state) => ({
    value: state.code,
    label: `${state.name} (${state.code})`,
  }));

  const placeholder = isLoadingStates
    ? intl.formatMessage(addressFormMessages.loadingStates)
    : intl.formatMessage(addressFormMessages.selectState);

  const optionsFilter: OptionsFilter = (input) => {
    const { options, search, limit } = input;
    const searchTerm = search.toLowerCase().trim();

    const filtered = options.reduce<ComboboxParsedItem[]>((acc, option) => {
      if ('group' in option) {
        const filteredItems = option.items.filter((item) => {
          const name = item.label?.toLowerCase() ?? '';
          const code = item.value?.toLowerCase() ?? '';
          return name.includes(searchTerm) || code.includes(searchTerm);
        });
        if (filteredItems.length > 0) {
          acc.push({ group: option.group, items: filteredItems });
        }
      } else {
        const name = option.label?.toLowerCase() ?? '';
        const code = option.value?.toLowerCase() ?? '';
        if (name.includes(searchTerm) || code.includes(searchTerm)) {
          acc.push(option);
        }
      }
      return acc;
    }, []);

    return limit > 0 ? filtered.slice(0, limit) : filtered;
  };

  // address-level1 is the HTML spec token for state/province/region
  const autoComplete = autocompleteSection ? `${autocompleteSection} address-level1` : 'address-level1';

  const handleChange = (val: string | null) => {
    onChange(val ?? '');
  };

  return (
    <AutofillSelect
      autoComplete={autoComplete}
      clearable
      data={selectData}
      data-testid={testId}
      disabled={disabled || isLoadingStates}
      error={error}
      filter={optionsFilter}
      label={label}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  );
};
